import { UserActivityType, ResourceType } from "../types/activity_types";
import {
  storeUserActivity,
  addChannelToHiddenList,
} from "../api_client/recommendation_api";
import * as Sentry from "@sentry/react";

// Interface for addUserActivity function
interface AddUserActivityFn {
  (activity: {
    resource_id: string;
    resource_type: ResourceType;
    activity_type: UserActivityType;
  }): void;
}

// Interface for removeUserActivity function
interface RemoveUserActivityFn {
  (resourceId: string, activityType: UserActivityType): void;
}

// Parameter interfaces for utility functions
interface SeeMoreLikeParams {
  resourceId: string;
  resourceType: string;
  addUserActivity: AddUserActivityFn;
  removeUserActivity: RemoveUserActivityFn;
  isResourceLiked: (resourceId: string) => boolean;
  onSuccess?: () => void;
  derived_from_resource_id?: string;
}

interface SeeLessDislikeParams {
  resourceId: string;
  resourceType: string;
  addUserActivity: AddUserActivityFn;
  removeUserActivity: RemoveUserActivityFn;
  startCollapsingResource: (resourceId: string) => void;
  derived_from_resource_id?: string;
}

interface HideChannelParams {
  channelId: string;
  channelName: string;
  resourceId?: string;
  hideChannel: (channelId: string) => void;
  startCollapsingResource: (resourceId: string) => void;
}

/**
 * Handle the "See more like this" action (like)
 */
export const handleSeeMoreLike = async ({
  resourceId,
  resourceType,
  addUserActivity,
  removeUserActivity,
  isResourceLiked,
  onSuccess,
  derived_from_resource_id = ""
}: SeeMoreLikeParams) => {
  console.log("See more like this clicked for resource:", resourceId);
  console.log("Current like status:", isResourceLiked(resourceId));

  if (!resourceId) {
    const warning = "Cannot toggle like activity: missing resourceId";
    console.warn(warning);
    Sentry.captureMessage(warning, {
      level: "warning",
      tags: { action: UserActivityType.LIKED },
    });
    return;
  }

  try {
    const response = await storeUserActivity(
      resourceId,
      resourceType,
      UserActivityType.LIKED,
      derived_from_resource_id // Pass the derived_from_resource_id parameter
    );

    if (response.success) {
      console.log("Full like response:", response);
      if (response.soft_deleted === true) {
        // Activity was soft-deleted (toggled off)
        removeUserActivity(resourceId, UserActivityType.LIKED);
        console.log("Like removed:", response);
      } else {
        // Activity was added / toggled ON
        addUserActivity({
          resource_id: resourceId,
          resource_type: resourceType as ResourceType,
          activity_type: UserActivityType.LIKED,
        });
        console.log("Like added:", response);
      }

      if (onSuccess) {
        onSuccess();
      }
    }
  } catch (error) {
    console.error("Error toggling like activity:", error);
    Sentry.captureException(error, {
      tags: {
        activity_type: UserActivityType.LIKED,
        resource_type: resourceType,
        resource_id: resourceId,
      },
    });
  }
};

/**
 * Handle the "See less like this" action (dislike) with collapsing animation
 */
export const handleSeeLessDislike = async ({
  resourceId,
  resourceType,
  addUserActivity,
  removeUserActivity,
  startCollapsingResource,
  derived_from_resource_id = ""
}: SeeLessDislikeParams) => {
  console.log("See less items like this clicked");

  if (!resourceId) {
    const warning = "Cannot store dislike activity: missing resourceId";
    console.warn(warning);
    Sentry.captureMessage(warning, {
      level: "warning",
      tags: { action: UserActivityType.DISLIKED },
    });
    return;
  }

  try {
    const response = await storeUserActivity(
      resourceId,
      resourceType,
      UserActivityType.DISLIKED,
      derived_from_resource_id // Pass the derived_from_resource_id parameter
    );
    console.log("See less response:", response);

    if (response.success) {
      // Start the collapse animation using the store
      startCollapsingResource(resourceId);
    }
  } catch (error) {
    console.error("Error storing dislike activity:", error);
    Sentry.captureException(error, {
      tags: {
        activity_type: UserActivityType.DISLIKED,
        resource_type: resourceType,
        resource_id: resourceId,
      },
    });
  }
};

/**
 * Handle the "Hide channel" action with collapsing animation
 */
export const handleHideChannel = async ({
  channelId,
  channelName,
  resourceId,
  hideChannel,
  startCollapsingResource
}: HideChannelParams) => {
  console.log(`Hide all items from ${channelName} clicked`);

  // Start the collapse animation using the store if resourceId is available
  if (resourceId) {
    startCollapsingResource(resourceId);
  }

  // Call the backend API to add the channel to the hidden list
  if (channelId) {
    try {
      const response = await addChannelToHiddenList(channelId);
      console.log("Hide channel response:", response);
      hideChannel(channelId);
    } catch (error) {
      console.error("Error hiding channel:", error);
      Sentry.captureException(error, {
        tags: { action: "hideChannel", channelId },
      });
      // Still update even if API call fails to maintain UI consistency
      hideChannel(channelId);
    }
  } else {
    const warning = "Cannot hide channel: missing channelId";
    console.warn(warning);
    Sentry.captureMessage(warning, {
      level: "warning",
      tags: { action: "hideChannel" },
    });
  }
};
